import { AgentInput, ReasoningStep, SuggestedQuestion } from './actions';
import { CitationSource } from './citationTypes';
import { ClarificationCard, ClarificationResolution, ClarificationResponse, StopRunResponse } from './clarificationTypes';
import {
    CHAT_CONFIG,
    clearMemoryService,
    downloadAskAicaFileService,
    getAskAicaFilesService,
    getChatHistoryService,
    getOrgConversationsService,
    getReattachStreamUrl,
    sendChatRequest,
    stopAskAicaRunService,
} from './service';
import { REATTACH_BACKOFF_MS, REATTACH_MAX_WALL_MS } from './constants';
import { ClaimedAttachment } from './types';
import { parseErrorCode } from '../../../utils/commonUtil';

interface Message {
    role: string;
    content: string;
}

/**
 * A single generated-file attachment, as emitted by Athena's build_attachment_metadata
 * (app/util/attachment_util.py, consumed by chat_service.py::_finalize_file_attachment /
 * _finalize_tool_attachments). mnemosyne_doc_id/source are null when the file wasn't
 * durably persisted (e.g. Mnemosyne upload failure) — such files never reach the
 * Library listing endpoint either, so they must not be optimistically added to it.
 */
export interface GeneratedFileAttachment {
    file_url: string;
    file_name: string;
    file_type: string;
    mnemosyne_doc_id: string | null;
    source: string | null;
}

interface StreamChunk {
    delta: {
        role: string;
        content: string;
    };
    message: {
        role: string;
        content: string;
    };
    is_final: boolean;
    /** Stamped by Athena on every chunk once the conversation resolves (Conversations v2). */
    conversation_id?: string;
    /** Stamped on the FIRST chunk — the handle /chat/stop and the re-attach
     * stream both take (RECUR-28136 §4.1/§4.5). */
    user_message_id?: string;
    suggested_questions?: SuggestedQuestion[];
    has_suggested_questions?: boolean;
    reasoning?: ReasoningStep[] | null;
    has_new_reasoning?: boolean;
    metadata: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
        /** Citation sources — present ONLY on the final chunk (is_final: true). */
        sources?: CitationSource[];
        // Present on the completion chunk when the response generated a file (e.g. a one-pager).
        file_url?: string;
        // Full per-file detail (including mnemosyne_doc_id/source) for the Library list —
        // the top-level file_url/file_name/file_type above only mirror the first entry.
        attachments?: GeneratedFileAttachment[];
        /** Filing state of this turn's own uploads — never read as `attachments` (Library). */
        uploaded_attachments?: ClaimedAttachment[];
        /** True on the feedback ack chunk — drives the distinct "Feedback received" bubble. */
        feedback_ack?: boolean;
        /** The paused round's card (paused turn's final chunk) / the outcome of the round a
         * resume answered / a resume that lost the double-answer race. */
        clarification?: ClarificationCard;
        clarification_resolution?: ClarificationResolution;
        clarification_stale?: boolean;
        /** The row's settled terminal status — present on the final chunk only
         * (live and re-attach alike, RECUR-28136). */
        status?: string;
    };
}

export interface AskAicaResponse {
    /** Captured off the stream's top-level chunk field (Conversations v2) — the
     * conversation this turn was saved against (server-created or resumed). */
    conversationId?: string;
    content: string;
    suggestedQuestions?: SuggestedQuestion[];
    hasNewSuggestedQuestions?: boolean;
    reasoning?: ReasoningStep[] | null;
    hasNewReasoning?: boolean;
    metadata?: {
        llm_used: string;
        content_chunk: boolean;
        timestamp: number;
        step_type?: 'thinking' | 'searching' | 'error';
        /** Citation sources — present ONLY on the final chunk (is_final: true). */
        sources?: CitationSource[];
        // Present on the completion chunk when the response generated a file (e.g. a one-pager).
        file_url?: string;
        attachments?: GeneratedFileAttachment[];
        /** Filing state of this turn's own uploads — never read as `attachments` (Library). */
        uploaded_attachments?: ClaimedAttachment[];
        /** True on the feedback ack chunk — drives the distinct "Feedback received" bubble. */
        feedback_ack?: boolean;
        /** The paused round's card (paused turn's final chunk) / the outcome of the round a
         * resume answered / a resume that lost the double-answer race. */
        clarification?: ClarificationCard;
        clarification_resolution?: ClarificationResolution;
        clarification_stale?: boolean;
        /** The row's settled terminal status — present on the final chunk only
         * (live and re-attach alike, RECUR-28136). */
        status?: string;
    };
}

/** Mutable state tracked across SSE chunks for a single streaming request. */
interface StreamState {
    isFirstChunk: boolean;
    suggestedQuestions: SuggestedQuestion[] | undefined;
    reasoning: ReasoningStep[] | null;
    hasNewReasoning: boolean;
    metadata: StreamChunk['metadata'] | undefined;
    lastServerContent: string;
    conversationId?: string;
    userMessageId?: string;
}

/** Parse SSE-formatted lines and invoke `onParsed` for each valid data frame.
 * Exported: this is a pure line-parser with no leads-state coupling — the
 * global (org-less) Ask AICA slice reuses it rather than re-implementing SSE
 * parsing (see store/globalAskAica/apiHelper.ts).
 *
 * `onId` receives the native SSE `id:` field, which the re-attach stream uses as
 * its reconnect cursor; it is optional so the two POST streamers — which are sent
 * no ids — stay unchanged. The id is reported when its LINE is seen, i.e. just
 * before the data frame it labels. That is safe here because every re-attach
 * frame carries the full accumulated content and the client replaces rather than
 * appends, so a cursor that skips one frame loses nothing. */
export const parseSSELines = <T = any>(lines: string[], onParsed: (data: T) => void, onId?: (id: string) => void) => {
    for (const line of lines) {
        if (onId && line.startsWith('id:')) {
            const id = line.slice(3).trim();
            if (id) onId(id);
            continue;
        }
        if (!line.startsWith('data:')) continue;
        if (line.includes('[DONE]')) continue;

        try {
            const jsonData = line.slice(5).trim();
            if (jsonData) {
                onParsed(JSON.parse(jsonData));
            }
        } catch (error) {
            console.error('Error parsing SSE data:', error, line);
        }
    }
};

/**
 * Incremental XHR/SSE line reader — the byte-accounting shared by the live POST
 * stream (streamAskAicaChat) and the GET re-attach stream (openReattachStream),
 * which used to hand-copy this bookkeeping and drift apart (RECUR-28136).
 * Tracks how much of `xhr.responseText` has been consumed and any trailing
 * partial line across successive readyState ticks, handing each tick's
 * complete lines to `onLines`.
 */
export const createXhrLineReader = (onLines: (lines: string[]) => void) => {
    let processedLength = 0; // How much of xhr.responseText we have consumed
    let lineBuffer = ''; // Carries a partial line (no trailing \n yet) across ticks

    return {
        /** Call on every readyState 3/4 tick with the XHR's current responseText. */
        readNewLines: (responseText: string) => {
            const newData = responseText.substring(processedLength);
            processedLength = responseText.length;
            if (!newData) return;

            const lines = (lineBuffer + newData).split('\n');
            // The last element may be an incomplete line (no trailing \n).
            // Hold it back until the next tick completes it.
            lineBuffer = lines.pop() || '';
            if (lines.length) onLines(lines);
        },
        /** Call once the stream is done, for callers that treat a trailing
         * partial line (never terminated by \n) as a complete line in its own
         * right. Callers that don't want this simply never call it. */
        flushTrailingLine: () => {
            if (lineBuffer.trim()) onLines([lineBuffer]);
            lineBuffer = '';
        },
    };
};

/**
 * Process a single parsed SSE chunk — updates tracking state and calls onChunk.
 * Uses server-accumulated `message.content` as the source of truth so the
 * reducer can replace (not append) and self-heal if any earlier chunk was lost.
 */
const processStreamChunk = (
    parsedData: StreamChunk,
    state: StreamState,
    onChunk: (
        chunk: string,
        suggestedQuestions?: SuggestedQuestion[],
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        isFirst?: boolean,
        metadata?: any,
        conversationId?: string,
    ) => void,
) => {
    if (parsedData.message && parsedData.message.content) {
        state.lastServerContent = parsedData.message.content;
    }

    if (parsedData.conversation_id) state.conversationId = parsedData.conversation_id;
    // Stamped on the first chunk. Carried through onChunk's metadata so it lands on
    // the assistant message in Redux, which is where Stop reads it from — the local
    // abort no longer stops the server-side run (RECUR-28136 D6/D2).
    if (parsedData.user_message_id) state.userMessageId = parsedData.user_message_id;

    if (parsedData.suggested_questions && parsedData.has_suggested_questions) {
        state.suggestedQuestions = parsedData.suggested_questions;
    }

    state.reasoning = parsedData.reasoning ?? null;
    state.hasNewReasoning = parsedData.has_new_reasoning ?? false;

    // Merge (not replace-once): fields like file_url only arrive on the
    // completion chunk, after earlier chunks have already set llm_used/timestamp.
    // Replacing wholesale on the first chunk (the old behavior) meant onComplete's
    // response.metadata never carried file_url, so a newly generated file's
    // Library refetch never fired.
    state.metadata = { ...state.metadata, ...parsedData.metadata };

    // Check for error in reasoning steps
    let isError = false;
    let errorMessage = '';
    if (Array.isArray(parsedData.reasoning)) {
        const errorReason = parsedData.reasoning.find((step) => step.type === 'error');
        if (errorReason) {
            isError = true;
            errorMessage = errorReason.message;
        }
    }

    const chunkMetadata = {
        ...parsedData.metadata,
        ...(state.userMessageId && { user_message_id: state.userMessageId }),
        ...(isError && { error: true, errorMessage }),
    };

    const currentIsFirst = state.isFirstChunk;
    if (state.isFirstChunk) state.isFirstChunk = false;

    onChunk(
        state.lastServerContent,
        parsedData.suggested_questions,
        parsedData.reasoning,
        state.hasNewReasoning,
        currentIsFirst,
        chunkMetadata,
        state.conversationId,
    );
};

/**
 * Helper function to send messages to the Ask Aica API with streaming enabled
 *
 * This implementation uses XMLHttpRequest for better control over the streaming request
 * to avoid any browser quirks with fetch API and SSE implementation
 *
 * @param messages Array of messages in the conversation
 * @param onChunk Callback that receives each chunk of the stream
 * @param onComplete Callback that is called when the stream is complete
 * @param onError Callback for error handling
 * @returns A function to abort the stream
 */
export const streamAskAicaChat = (
    messages: Message[],
    attachmentDocIds: string[] | undefined,
    user_id: string,
    org_id: string,
    onChunk: (
        chunk: string,
        suggestedQuestions?: SuggestedQuestion[],
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        isFirst?: boolean,
        metadata?: any,
        conversationId?: string,
    ) => void,
    onComplete: (response: AskAicaResponse) => void,
    onError: (error: any) => void,
    flowKey?: string,
    agentInput?: AgentInput,
    user_name?: string,
    conversationId?: string,
    source?: string,
    newConversation?: boolean,
    clarificationResponse?: ClarificationResponse,
): (() => void) => {
    const xhr = new XMLHttpRequest();

    const state: StreamState = {
        isFirstChunk: true,
        suggestedQuestions: undefined,
        reasoning: null,
        hasNewReasoning: false,
        metadata: undefined,
        lastServerContent: '',
    };
    const handleParsedChunk = (parsedData: StreamChunk) => processStreamChunk(parsedData, state, onChunk);
    const lineReader = createXhrLineReader((lines) => parseSSELines(lines, handleParsedChunk));

    xhr.open('POST', CHAT_CONFIG.URL);

    const headers = CHAT_CONFIG.getHeaders(org_id, user_id);
    Object.keys(headers).forEach((key) => {
        xhr.setRequestHeader(key, headers[key]);
    });

    xhr.onreadystatechange = function () {
        if (xhr.readyState === 3 || xhr.readyState === 4) {
            lineReader.readNewLines(xhr.responseText);
        }

        if (xhr.readyState === 4) {
            // Flush any remaining data in the line buffer
            lineReader.flushTrailingLine();

            if (xhr.status >= 200 && xhr.status < 300) {
                onComplete({
                    content: state.lastServerContent,
                    conversationId: state.conversationId,
                    suggestedQuestions: state.suggestedQuestions,
                    hasNewSuggestedQuestions: !!state.suggestedQuestions,
                    reasoning: state.reasoning,
                    hasNewReasoning: state.hasNewReasoning,
                    metadata: state.metadata,
                });
            } else {
                // status/responseSnippet let a caller distinguish e.g. a stale-id 404
                // (A-29) from a generic failure — mirrors the portfolio streamer
                // (globalAskAica/apiHelper.ts).
                const error = new Error(`HTTP error! Status: ${xhr.status}`);
                (error as any).status = xhr.status;
                (error as any).responseSnippet = xhr.responseText?.slice(0, 500);
                (error as any).code = parseErrorCode(xhr.responseText);
                onError(error);
            }
        }
    };

    xhr.onerror = function () {
        const error = new Error('Network error occurred');
        (error as any).status = xhr.status;
        (error as any).responseSnippet = xhr.responseText?.slice(0, 500);
        onError(error);
    };

    xhr.send(
        JSON.stringify({
            user_id,
            org_id,
            messages,
            stream: true,
            flow_key: flowKey ?? 'user_chat',
            ...(attachmentDocIds?.length ? { attachment_doc_ids: attachmentDocIds } : {}),
            ...(agentInput && { agent_input: agentInput }),
            ...(user_name && { user_name }),
            ...(conversationId && { conversation_id: conversationId }),
            ...(source && { source }),
            // Defense-in-depth (D1): the flag must never ride along with an explicit
            // id — callers already guarantee this, the builder now enforces it too.
            ...(newConversation && !conversationId && { new_conversation: true }),
            ...(clarificationResponse && { clarification_response: clarificationResponse }),
        }),
    );

    return () => {
        xhr.abort();
    };
};

/**
 * Helper function to send messages to the Ask Aica API without streaming
 * @param messages Array of messages in the conversation
 * @returns Promise with the response
 */
export const askAicaChat = async (messages: Message[]): Promise<AskAicaResponse> => {
    try {
        const response = await sendChatRequest({ messages });
        return {
            content: response.data.content,
            metadata: response.data.metadata,
        };
    } catch (error) {
        console.error('Error in askAicaChat:', error);
        throw error;
    }
};

export const getChatHistory = async (userId, orgId, pageNum, pageSize, conversationId?: string) => {
    try {
        const response = await getChatHistoryService({ userId, orgId, pageNum, pageSize, conversationId });
        return response.data;
    } catch (err) {
        // Never log the raw error — an AxiosError's `config.headers` carries the
        // session cerberus token (see useAskAicaFileActions.ts::openFile).
        console.error('Error in getChatHistory:', err instanceof Error ? err.message : 'unknown error');
        throw err;
    }
};

// userId and orgId feed auth headers only — the server authorizes from trusted header context.
export const getOrgConversations = async (
    userId: string,
    orgId: string,
    handlers: { onSuccess: (data: any) => void; onError?: (err: any) => void },
) => {
    try {
        const response = await getOrgConversationsService({ userId, orgId });
        handlers.onSuccess(response.data);
    } catch (err) {
        // Never log the raw error — an AxiosError's `config.headers` carries the
        // session cerberus token (see useAskAicaFileActions.ts::openFile).
        console.error('Error in getOrgConversations:', err instanceof Error ? err.message : 'unknown error');
        handlers.onError?.(err);
    }
};

/**
 * Download a generated file from Athena and trigger a browser save dialog.
 * Auth + URL construction are handled by the service layer (axios client).
 * `orgId` is the context org the chat ran under (see downloadAskAicaFileService).
 * Throws on failure so the caller can surface a user-facing error.
 */
export const downloadAskAicaFile = async (fileUrl: string, fileName: string, orgId?: string): Promise<void> => {
    const response = await downloadAskAicaFileService(fileUrl, orgId);
    const objectUrl = window.URL.createObjectURL(response.data);
    try {
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        window.URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Fetch a generated file's bytes from Athena as a Blob, for feeding straight into
 * an in-app viewer (PdfViewer/SheetEditorDrawer/DocxViewer) instead of a browser
 * download. Auth + SSRF guarding are handled by downloadAskAicaFileService — see
 * its doc comment. `orgId` MUST be the context org the chat ran under, or Athena
 * returns 403.
 */
export const fetchAskAicaFileBlob = async (fileUrl: string, orgId?: string): Promise<Blob> => {
    const response = await downloadAskAicaFileService(fileUrl, orgId);
    return response.data;
};

/**
 * Fetch the Library file list for an org via Polus, using the onSuccess/onError
 * handler convention used elsewhere in the app (see MagicUploadHelper.getUntaggedCountPerUser)
 * instead of a caller-side .then()/.catch(). Empty-on-error: never throws to the
 * UI — Library shows an empty state rather than surfacing a transient fetch
 * failure on top of the chat. onSuccess runs inside the same try block, so a
 * failure in the caller's own handling (e.g. malformed-entry mapping) is also
 * routed to onError rather than left unhandled.
 */
export const getAskAicaFiles = async (orgId: string, handlers: { onSuccess: (files: any[]) => void; onError?: () => void }) => {
    try {
        const response = await getAskAicaFilesService(orgId);
        const files = response?.data?.responseBody?.files || [];
        handlers.onSuccess(files);
    } catch (err) {
        console.error('Error fetching Ask AICA library files:', err);
        handlers.onError?.();
    }
};

export const clearMemory = async (userId: string, orgId?: string, conversationId?: string) => {
    try {
        const response = await clearMemoryService({ userId, orgId, conversationId });
        return response.data;
    } catch (err) {
        console.error('Error in clearMemory:', err);
        throw err;
    }
};

/** A frame from the re-attach stream. It is an ordinary chat SSE chunk — the
 * server rebuilds `message.content` as the FULL accumulated answer on every
 * frame, so both surfaces' existing replace-don't-append handling works
 * unchanged (RECUR-28136 §4.5). */
export interface ReattachFrame {
    message?: { role: string; content: string };
    is_final?: boolean;
    conversation_id?: string;
    user_message_id?: string;
    reasoning?: ReasoningStep[] | null;
    has_new_reasoning?: boolean;
    metadata?: Record<string, any>;
}

/** Why a re-attach stopped. `done` = the run reached its final frame; `fatal` =
 * 403/404/422, nothing to follow; `exhausted` = REATTACH_MAX_WALL_MS elapsed
 * while the run was still in progress (NOT a failure — see ASK_AICA_WORKING_COPY). */
export type ReattachOutcome = 'done' | 'fatal' | 'exhausted';

export interface ReattachStreamHandlers {
    /** Auth headers for the GET. Supplied by the surface: the portfolio surface
     * must never send `x-organization-id`. */
    headers: Record<string, string>;
    onFrame: (frame: ReattachFrame) => void;
    /** The buffer is gone (410) or its backend is unreachable (503). LATCHED —
     * fires once on the transition into the degraded state and never again, so
     * the one-shot history fallback can never compose into a poll (D16). */
    onDegraded: () => void;
    onEnd: (outcome: ReattachOutcome) => void;
}

/**
 * Live re-attach streams, keyed on user_message_id. MODULE-level on purpose: it
 * must survive a hook remount AND be shared by the desktop and mobile
 * useAskAica() instances and by both surfaces, none of which can see each
 * other's state. This is the cross-instance half of the no-double-stream rule
 * (RECUR-28136 D12 / A-57d); the other half is the caller's `!isLoading` term.
 */
const reattachStreams = new Set<string>();

/**
 * Follow an already-running turn over SSE-on-XHR, reconnecting from the last
 * received `id:` on a drop. GET, because it is an idempotent read; XHR rather
 * than EventSource, because auth is the `x-cerberus-token` HEADER and
 * EventSource cannot set headers.
 *
 * Returns a close function the caller can hold onto to stop following this run
 * early (e.g. New Chat, delete, or a conversation switch) — closing tears the
 * connection down WITHOUT firing `onEnd`, since the caller already knows why it
 * stopped. Returns null — and does nothing else — when this run is already
 * being followed.
 */
export const openReattachStream = (userMessageId: string, handlers: ReattachStreamHandlers): (() => void) | null => {
    if (reattachStreams.has(userMessageId)) return null;

    let cursor: string | undefined;
    let attempt = 0;
    let degraded = false;
    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let activeXhr: XMLHttpRequest | undefined;
    const startedAt = Date.now();

    // Common teardown for every exit path — clears the retry timer, closes the
    // socket rather than leaving it draining into a responseText nobody reads,
    // and frees the registry slot. `finish` layers the onEnd callback on top;
    // an early `close()` deliberately skips it (the caller already knows why).
    const teardown = () => {
        stopped = true;
        if (retryTimer) clearTimeout(retryTimer);
        activeXhr?.abort();
        reattachStreams.delete(userMessageId);
    };

    const finish = (outcome: ReattachOutcome) => {
        if (stopped) return;
        teardown();
        handlers.onEnd(outcome);
    };

    const scheduleReconnect = () => {
        if (stopped) return;
        if (Date.now() - startedAt >= REATTACH_MAX_WALL_MS) {
            finish('exhausted');
            return;
        }
        const delay = REATTACH_BACKOFF_MS[Math.min(attempt, REATTACH_BACKOFF_MS.length - 1)];
        attempt += 1;
        retryTimer = setTimeout(connect, delay);
    };

    function connect() {
        if (stopped) return;
        const xhr = new XMLHttpRequest();
        activeXhr = xhr;
        let sawDone = false;

        const consume = (lines: string[]) => {
            if (lines.some((line) => line.includes('[DONE]'))) sawDone = true;
            parseSSELines<ReattachFrame>(
                lines,
                (frame) => {
                    // A frame arrived, so this connection is healthy: reset the
                    // backoff so a later drop reconnects promptly.
                    attempt = 0;
                    handlers.onFrame(frame);
                },
                (id) => {
                    cursor = id;
                },
            );
        };
        const lineReader = createXhrLineReader(consume);

        xhr.open('GET', getReattachStreamUrl(userMessageId, cursor));
        Object.keys(handlers.headers).forEach((key) => {
            xhr.setRequestHeader(key, handlers.headers[key]);
        });

        xhr.onreadystatechange = () => {
            if (stopped) return;
            const ok = xhr.status >= 200 && xhr.status < 300;
            if (ok && (xhr.readyState === 3 || xhr.readyState === 4)) {
                lineReader.readNewLines(xhr.responseText);
            }

            if (xhr.readyState !== 4) return;

            if (ok) {
                lineReader.flushTrailingLine();
                if (sawDone) finish('done');
                else scheduleReconnect();
                return;
            }
            // 410 (buffer gone) and 503 (backend down) read identically to the
            // client: take the one-shot history fallback, then keep retrying.
            if (xhr.status === 410 || xhr.status === 503) {
                if (!degraded) {
                    degraded = true;
                    handlers.onDegraded();
                }
                // A 410 means this cursor can never be served again — re-ask for
                // the full catch-up instead.
                cursor = undefined;
                scheduleReconnect();
                return;
            }
            if (xhr.status === 403 || xhr.status === 404 || xhr.status === 422) {
                finish('fatal');
                return;
            }
            scheduleReconnect();
        };

        xhr.onerror = () => {
            if (!stopped) scheduleReconnect();
        };

        xhr.send();
    }

    reattachStreams.add(userMessageId);
    connect();
    return () => {
        if (stopped) return;
        teardown();
    };
};

/** Stop an in-flight run server-side. Idempotent by contract, so a second press
 * is a harmless 2xx. Rethrows so the caller decides what the user sees. */
export const stopAskAicaRun = async (userMessageId: string, headers: Record<string, string>): Promise<StopRunResponse> => {
    try {
        const response = await stopAskAicaRunService(userMessageId, headers);
        return response.data;
    } catch (err) {
        // Never log the raw error — an AxiosError's `config.headers` carries the
        // session cerberus token (see useAskAicaFileActions.ts::openFile).
        console.error('Error in stopAskAicaRun:', err instanceof Error ? err.message : 'unknown error');
        throw err;
    }
};
